import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { AppDispatch } from "slices/store";
import { useAppDispatch, useAppSelector } from "slices/hook";
import { getAuthState } from "slices/auth/login/reducer";

import Flatpickr from "react-flatpickr";
import Select from "react-select";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import moment from "moment";

import LoadingComponent from "common/components/loader";

import { AppUtility } from "utility/app-utility";
import { BrandingData } from "admin-portal/settings/branding";

import { SubcriptionStatuses, TenantTypes } from "models/settings/branding";
import { SubscriptionPlan } from "models/subscription/subcription-plan";
import { TenantSubscriptionPlanStatus, TenantType, UserRoles } from "models/enums";
import { Tenant } from "models/tenant/tenant";

import { CreateTenant, GetSubscriptionPlans, UpdateTenant } from "api/tenant";

const validationSchema = Yup.object().shape({
  companyName: Yup.string().required("Company Name is required"),
  companyEmail: Yup.string().email("Invalid email format").required("Company Email is required"),
  contactNumber: Yup.string().required("Contact Number is required"),
  brandingName: Yup.string().required("Branding Name is required"),
  // domain: Yup.string().required("Website Domain is required"),
  subscription: Yup.object().shape({
    label: Yup.string().required(`Subscription is required`),
    value: Yup.string(),
  }),
  validTill: Yup.date()
    .nullable()
    .default(null)
    .test("is-valid-date", "Invalid date format", function (value) {
      if (value === null) return true; // Allow null values
      return moment(value, true).isValid(); // Use moment.js to check if the date is valid
    }),
});

interface Props {
  tenant?: Tenant;
  onTenantSaveSuccess: (tenant: Tenant) => void;
}

const TenantInforComponent: React.FC<Props> = ({ tenant, onTenantSaveSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const [editMode, setEditMode] = useState(false);

  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<{ value: string; label: string }[]>([]);

  const [systemTenant, setSystemTenant] = useState(false);
  const dispatch: AppDispatch = useAppDispatch();
  const { loggedInuser } = useAppSelector(getAuthState);

  useEffect(() => {
    const _isSystem = tenant?.tenantTypeId == TenantType.SYSTEM;
    setSystemTenant(_isSystem);
    loadSubscription();
  }, []);
  useEffect(() => {
    if (tenant && subscriptions.length > 0) {
      var _stSort = (tenant?.subscriptions ?? []).sort((a, b) =>
        a.insertedTime! > b.insertedTime! ? -1 : a.insertedTime! < b.insertedTime! ? 1 : 0
      );
      var _lastSubs = _stSort.length > 0 ? _stSort![0] : null;
      if (_lastSubs) {
        var _sts = SubcriptionStatuses.find(
          (s) => s.value == (_lastSubs?.status ?? TenantSubscriptionPlanStatus.ACTIVATED)
        );
        var _sub = subscriptions.find((s) => s.value == _lastSubs?.subscriptionPlan?.id);
        formik.setFieldValue("subscription", _sub);
        formik.setFieldValue("subscriptionStatus", _sts);
        formik.setFieldValue(
          "subscriptionDue",
          _lastSubs && (_lastSubs?.paymentDueDate ?? "") != ""
            ? AppUtility.ISOStringToDate(_lastSubs?.paymentDueDate!)
            : null
        );
      }
    }
  }, [subscriptions]);
  const loadSubscription = async () => {
    setLoading(true);
    const res = await GetSubscriptionPlans();
    if (!(res?.status?.isError ?? false) && res?.data) {
      setSubscriptionPlans(res?.data);

      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      setSubscriptions(_st);
    } else {
      setSubscriptions([]);
    }
    setLoading(false);
  };

  var _stSort = (tenant?.subscriptions ?? []).sort((a, b) =>
    a.insertedTime! > b.insertedTime! ? -1 : a.insertedTime! < b.insertedTime! ? 1 : 0
  );
  var _lastSubs = _stSort.length > 0 ? _stSort![0] : null;

  const formik: any = useFormik({
    initialValues: {
      companyName: "",
      companyEmail: "",
      contactNumber: "",
      brandingName: "",
      domain: "",
      webSiteName: "",
      isActive: true,
      mfaEnabled: true,
      //emailVerification: false,

      openingTime: "",
      closingTime: "",
      enableEmailNotification: false,
      enableSMSNotification: false,

      tenantType: TenantTypes.find((t) => t.value === TenantType.TRIAL),
      subscription: _lastSubs ? subscriptions.find((s) => s.value == _lastSubs?.subscriptionPlan?.id) : null,
      subscriptionStatus: _lastSubs
        ? subscriptions?.find((t) => t.value === _lastSubs?.status)
        : subscriptions?.find((t) => t.value === TenantSubscriptionPlanStatus.ACTIVATED),
      subscriptionDue:
        _lastSubs && (_lastSubs?.paymentDueDate ?? "") != ""
          ? AppUtility.ISOStringToDate(_lastSubs?.paymentDueDate!)
          : null,
      ...tenant, // Spread the fetched tenant data if available
      validTill: (tenant?.validTill ?? "") != "" ? AppUtility.ISOStringToDate(tenant?.validTill!) : null,
    },
    validationSchema: validationSchema,
    // Validation function to check the entire form
    validate: async (values: typeof formik.values) => {
      try {
        await validationSchema.validate(values, { abortEarly: false });
        return {}; // Return empty object when validation succeeds
      } catch (err: any) {
        // Log errors to your database here
        console.error("Validation errors:", err.errors);
        // handleLogErrorToDB(errors.inner); // Custom function to log errors to DB
        return err.inner.reduce((errorObj: any, currentError: any) => {
          return {
            ...errorObj,
            [currentError.path]: currentError.message,
          };
        }, {} as Record<string, string>);
      }
    },

    onSubmit: async (values) => {
      try {
        setError("");
        setSuccess("");
        setLoading(true);

        var _selectedSubs = values.subscription! as { value: string; label: string };
        var _tenat: Tenant = {
          id: tenant?.id ?? undefined,
          companyName: values.companyName ?? "",
          companyEmail: values.companyEmail ?? "",
          contactNumber: values.contactNumber ?? "",
          brandingName: values.brandingName ?? "",
          domain: values.domain ?? "",
          webSiteName: values.webSiteName ?? "",
          isActive: values.isActive ?? true,
          mfaEnabled: values.mfaEnabled ?? true,
          //emailVerification: false,
          validTill: values.validTill ? AppUtility.ISODateToISOString(values.validTill) : null,
          openingTime: values.openingTime ?? "",
          closingTime: values.closingTime ?? "",
          enableEmailNotification: values.enableEmailNotification ?? false,
          enableSMSNotification: values.enableSMSNotification ?? false,

          tenantTypeId: values.tenantType?.value as TenantType,
          subscriptions: [
            {
              subscriptionPlan: {
                id: _selectedSubs.value ?? "",
                name: _selectedSubs.label ?? "",
              },
              paymentDueDate: values.subscriptionDue ? AppUtility.ISODateToISOString(values.subscriptionDue) : null,
              status: values.subscriptionStatus?.value as TenantSubscriptionPlanStatus,
            },
          ],
        };
        const res = !editMode ? await CreateTenant(_tenat) : await UpdateTenant(_tenat);

        if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
          setSuccess("success");
          setSelectedTenant(res!.data!);
          onTenantSaveSuccess(res!.data!);
          setEditMode(true);
        } else {
          setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
        }
        setLoading(false);
      } catch (error) {
        setError("Something went wrong...");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (tenant) {
      setSelectedTenant(tenant);
      formik.setValues(tenant); // Set formik values when tenant prop changes
      formik.setFieldValue(
        "validTill",
        (tenant?.validTill ?? "") != "" ? AppUtility.ISOStringToDate(tenant?.validTill!) : null
      );
      if (tenant) {
        var _stSort = (tenant?.subscriptions ?? []).sort((a, b) =>
          a.insertedTime! > b.insertedTime! ? -1 : a.insertedTime! < b.insertedTime! ? 1 : 0
        );
        var _lastSubs = _stSort.length > 0 ? _stSort![0] : null;

        if (_lastSubs) {
          var _st = SubcriptionStatuses.find(
            (s) => s.value == (_lastSubs?.status ?? TenantSubscriptionPlanStatus.ACTIVATED)
          );

          var _sub = subscriptions.find((s) => s.value == _lastSubs?.subscriptionPlan?.id);
          formik.setFieldValue("subscription", _sub);
          formik.setFieldValue("subscriptionStatus", _st);
          //formik.setFieldValue("subscriptionDue", _lastSubs.paymentDueDate ?? null);
          formik.setFieldValue(
            "subscriptionDue",
            _lastSubs && (_lastSubs?.paymentDueDate ?? "") != ""
              ? AppUtility.ISOStringToDate(_lastSubs?.paymentDueDate!)
              : null
          );
        }
      }

      setEditMode((tenant?.id ?? "").length > 0);
    }
  }, [tenant]);

  const onSubscriptionChange = (option: { value: string; label: string }) => {
    formik.setFieldValue("subscription", option);
    const _selectSub = subscriptionPlans.find((s) => s.id == option.value);
    if (_selectSub) {
      if ((_selectSub.endDate ?? "") != "") {
        formik.setFieldValue("validTill", AppUtility.ISOStringToDate(_selectSub.endDate ?? ""));
      } else if ((_selectSub.validNoOfDays ?? 0) > 0) {
        var _dt = new Date();
        _dt.setDate(_dt.getDate() + (_selectSub.validNoOfDays ?? 0));
        formik.setFieldValue("validTill", _dt);
      }
    }
  };
  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-12">
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="companyName" className="inline-block mb-2 text-base font-medium">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.companyName ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.companyName}
              />
              {formik.errors.companyName && <p className="text-red-500 text-sm mt-1">{formik.errors.companyName}</p>}
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="companyEmail" className="inline-block mb-2 text-base font-medium">
                Company Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="companyEmail"
                name="companyEmail"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.companyEmail ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.companyEmail}
              />
              {formik.errors.companyEmail && <p className="text-red-500 text-sm mt-1">{formik.errors.companyEmail}</p>}
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="contactNumber" className="inline-block mb-2 text-base font-medium">
                Contact Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="contactNumber"
                name="contactNumber"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.contactNumber ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.contactNumber}
              />
              {formik.errors.contactNumber && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.contactNumber}</p>
              )}
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="tenantType" className="inline-block mb-2 text-base font-medium">
                {BrandingData.bussinessDomain} Category <span className="text-red-500">*</span>
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={TenantTypes}
                isSearchable={false}
                name="tenantType"
                id="tenantType"
                value={formik.values.tenantType}
                onChange={(option) => formik.setFieldValue("tenantType", option)}
              />
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="brandingName" className="inline-block mb-2 text-base font-medium">
                Branding Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="brandingName"
                name="brandingName"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.brandingName ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.brandingName}
              />
              {formik.errors.brandingName && <p className="text-red-500 text-sm mt-1">{formik.errors.brandingName}</p>}
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="domain" className="inline-block mb-2 text-base font-medium">
                Domain/Sub Domain
              </label>
              <input
                type="text"
                id="domain"
                name="domain"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.domain ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.domain}
              />
              {formik.errors.domain && <p className="text-red-500 text-sm mt-1">{formik.errors.domain}</p>}
            </div>
          </div>

          <div className="xl:col-span-4">
            <div>
              <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                Active
              </label>
              <div className="flex items-center">
                <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    id="isActive"
                    className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                    onChange={formik.handleChange}
                    checked={formik.values.isActive}
                  />
                  <label
                    htmlFor="isActive"
                    className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                  ></label>
                </div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-4">
            <div>
              <label htmlFor="mfaEnabled" className="inline-block mb-2 text-base font-medium">
                MFA required
              </label>
              <div className="flex items-center">
                <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                  <input
                    type="checkbox"
                    name="mfaEnabled"
                    id="mfaEnabled"
                    className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                    onChange={formik.handleChange}
                    checked={formik.values.mfaEnabled}
                  />
                  <label
                    htmlFor="mfa"
                    className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                  ></label>
                </div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-4">
            {
              <div>
                <label htmlFor="notifications" className="inline-block mb-2 text-base font-medium">
                  Notifications
                </label>
                <div className="flex items-center  ">
                  <div className="grid grid-cols-2 gap-4 w-full">
                    <div className="flex items-center">
                      <label htmlFor="enableSMSNotification" className="inline-block mb-2 text-base font-medium mr-2">
                        SMS
                      </label>
                      <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                        <input
                          type="checkbox"
                          name="enableSMSNotification"
                          id="enableSMSNotification"
                          className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                          onChange={formik.handleChange}
                          checked={formik.values.enableSMSNotification}
                        />
                        <label
                          htmlFor="enableSMSNotification"
                          className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                        ></label>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <label htmlFor="enableEmailNotification" className="inline-block mb-2 text-base font-medium mr-2">
                        Email
                      </label>
                      <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                        <input
                          type="checkbox"
                          name="enableEmailNotification"
                          id="enableEmailNotification"
                          className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                          onChange={formik.handleChange}
                          checked={formik.values.enableEmailNotification}
                        />
                        <label
                          htmlFor="enableEmailNotification"
                          className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>

          <div className="mb-4 xl:col-span-4">
            <label htmlFor="fromInpuSubscription" className="inline-block mb-2 text-base font-medium">
              Subscription<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 focus:outline-none focus:border-custom-500"
              options={subscriptions}
              isSearchable={false} // To disable search
              name="subscription"
              id="subscription"
              value={formik.values.subscription}
              onChange={onSubscriptionChange}
            />
            {formik.touched.subscription && formik.errors.subscription ? (
              <p id="subscription" className="mt-1 text-sm text-red-500">
                {formik.errors.subscription.label || formik.errors.subscription}
              </p>
            ) : null}
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="subscriptionStatus" className="inline-block mb-2 text-base font-medium">
                Subscription Status <span className="text-red-500">*</span>
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={SubcriptionStatuses}
                isSearchable={false}
                name="subscriptionStatus"
                id="subscriptionStatus"
                value={formik.values.subscriptionStatus}
                onChange={(option) => formik.setFieldValue("subscriptionStatus", option)}
              />
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="subscriptionDue" className="inline-block mb-2 text-base font-medium">
                Subscription Payment Due On
              </label>
              <Flatpickr
                id="subscriptionDue"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.subscriptionDue ? "border-red-500" : ""
                }`}
                options={{
                  dateFormat: "d M, Y",
                }}
                placeholder="Select Date"
                onChange={(date) => formik.setFieldValue("subscriptionDue", date[0])}
                value={formik.values.subscriptionDue ?? ""}
              />
              {formik.errors.subscriptionDue && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.subscriptionDue}</p>
              )}
            </div>
          </div>

          <div className="xl:col-span-4">
            <div>
              <label htmlFor="validTill" className="inline-block mb-2 text-base font-medium">
                Valid Till
              </label>
              <Flatpickr
                id="validTill"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.validTill ? "border-red-500" : ""
                }`}
                options={{
                  dateFormat: "d M, Y",
                }}
                placeholder="Select Date"
                onChange={(date) => formik.setFieldValue("validTill", date[0])}
                value={formik.values.validTill ?? ""}
              />
              {formik.errors.validTill && <p className="text-red-500 text-sm mt-1">{formik.errors.validTill}</p>}
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="openingTime" className="inline-block mb-2 text-base font-medium">
                Opening Time
              </label>
              <input
                type="text"
                id="openingTime"
                name="openingTime"
                placeholder="08:00"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.openingTime ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.openingTime}
              />
              {formik.errors.openingTime && <p className="text-red-500 text-sm mt-1">{formik.errors.openingTime}</p>}
            </div>
          </div>
          <div className="xl:col-span-4">
            <div>
              <label htmlFor="closingTime" className="inline-block mb-2 text-base font-medium">
                Closing Time
              </label>
              <input
                type="text"
                id="closingTime"
                name="closingTime"
                placeholder="20:00"
                className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 ${
                  formik.errors.closingTime ? "border-red-500" : ""
                }`}
                onChange={formik.handleChange}
                value={formik.values.closingTime}
              />
              {formik.errors.closingTime && <p className="text-red-500 text-sm mt-1">{formik.errors.closingTime}</p>}
            </div>
          </div>
        </div>
        {(!systemTenant || loggedInuser?.activeRole?.name == UserRoles.SYSTEM_SUPER_ADMIN) && (
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="reset"
              className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
              onClick={formik.handleReset}
            >
              Reset
            </button>
            <button
              type="submit"
              className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {editMode ? "Update" : "Create"}
            </button>
          </div>
        )}
      </form>
      {loading && <LoadingComponent />}
      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b>
          {!editMode ? (
            <>{BrandingData.bussinessDomain} has been created. Now you can add users to the system</>
          ) : (
            <> {BrandingData.bussinessDomain} has been updated. </>
          )}
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          {!editMode ? (
            <>
              Oops! Something went wrong while attempting to create {BrandingData.bussinessDomain.toLocaleLowerCase()}.
              Please try again later or contact support for assistance.
            </>
          ) : (
            <>
              Oops! Something went wrong while attempting to update {BrandingData.bussinessDomain.toLocaleLowerCase()}.
              Please try again later or contact support for assistance.
            </>
          )}
        </div>
      )}
    </>
  );
};

export default TenantInforComponent;
